/**
 * 例外規則的「月曆預覽」命中判斷（UI 專用的簡化重算）。
 *
 * 目的僅止於「讓月曆格狀預覽能標出哪一天被例外覆蓋」，語義對齊
 * `packages/core/src/engine/calendar-exceptions.ts` 的 None/Daily/Weekly/Monthly/Yearly
 * 規則，但這裡不依賴該內部模組（未從套件公開匯出），改以 UTC Date 運算獨立實作。
 * 真正的排程／工時運算一律由 core 的 `resolveCalendar` 負責，此檔案不參與運算路徑。
 */
import type { CalendarException } from '@bigint-project/core';

function toUtcDays(iso: string): number {
  const [y, m, d] = iso.split('-').map(Number);
  return Math.floor(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1) / 86_400_000);
}

function civilOf(days: number): { year: number; month: number; day: number } {
  const ms = days * 86_400_000;
  const date = new Date(ms);
  return { year: date.getUTCFullYear(), month: date.getUTCMonth() + 1, day: date.getUTCDate() };
}

function isValidCivil(year: number, month: number, day: number): boolean {
  if (month < 1 || month > 12 || day < 1) return false;
  const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return day <= daysInMonth;
}

/** 判斷 `dateIso` 是否落在此例外的任一次發生區間內；不命中回傳 false。 */
export function exceptionCoversDate(exception: CalendarException, dateIso: string): boolean {
  const day = toUtcDays(dateIso);
  const fromDay = toUtcDays(exception.from);
  const toDay = toUtcDays(exception.to);
  const spanDays = toDay - fromDay;
  if (day < fromDay) return false;

  const recurrence = exception.recurrence;
  const kind = recurrence?.kind ?? 'None';
  const untilDay = recurrence?.until === undefined ? null : toUtcDays(recurrence.until);
  const interval =
    recurrence === undefined || !Number.isFinite(recurrence.interval) || recurrence.interval < 1
      ? 1
      : Math.floor(recurrence.interval);

  const covers = (occurrenceStart: number): boolean => {
    if (untilDay !== null && occurrenceStart > untilDay) return false;
    const offset = day - occurrenceStart;
    return offset >= 0 && offset <= spanDays;
  };

  if (kind === 'None') {
    return day - fromDay <= spanDays;
  }

  if (kind === 'Daily' || kind === 'Weekly') {
    const stepDays = kind === 'Daily' ? interval : interval * 7;
    const latest = Math.floor((day - fromDay) / stepDays);
    const lookBack = Math.floor(spanDays / stepDays) + 1;
    for (let k = latest; k >= 0 && k >= latest - lookBack; k -= 1) {
      if (covers(fromDay + k * stepDays)) return true;
    }
    return false;
  }

  // Monthly / Yearly
  const stepMonths = kind === 'Monthly' ? interval : interval * 12;
  const fromCivil = civilOf(fromDay);
  const civil = civilOf(day);
  const monthsDiff = (civil.year - fromCivil.year) * 12 + (civil.month - fromCivil.month);
  const lookBack = Math.floor(spanDays / 28) + 1;
  for (let s = monthsDiff; s >= 0 && s >= monthsDiff - lookBack * stepMonths; s -= 1) {
    if (s % stepMonths !== 0) continue;
    const totalMonths = fromCivil.year * 12 + (fromCivil.month - 1) + s;
    const year = Math.floor(totalMonths / 12);
    const month = totalMonths - year * 12 + 1;
    if (!isValidCivil(year, month, fromCivil.day)) continue;
    const occurrenceStart = Math.floor(Date.UTC(year, month - 1, fromCivil.day) / 86_400_000);
    if (covers(occurrenceStart)) return true;
  }
  return false;
}

/** 在一組例外中找出「覆蓋此日期」者，依清單順序取第一個命中（子日曆的例外應排在前面）。 */
export function findCoveringException(
  exceptions: readonly CalendarException[],
  dateIso: string,
): CalendarException | null {
  for (const exception of exceptions) {
    if (exceptionCoversDate(exception, dateIso)) return exception;
  }
  return null;
}
